import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { StringCustomFieldConfig, SharedModule, FormInputComponent, NotificationService } from '@vendure/admin-ui/core';
import { safeJSONParse } from '../utils';
import { LayoutComponentType, LayoutOptionData } from '../ui-types';

@Component({
  selector: 'layout-option-input',
  template: `
    <div>
      <!-- Full Width Toggle -->
      <vdr-card [title]="'Display'">
        <div class="clr-row" style="align-items: flex-end;">
          <vdr-form-field [label]="'Section Width'" class="clr-col-6">
            <clr-toggle-wrapper>
              <input type="checkbox" clrToggle name="pageFullWidth" [formControl]="pageFullWidthControl" />
              <label class="ml-1">{{ pageFullWidthControl.value ? 'Section: Full Width' : 'Section: Contained Width' }}</label>
            </clr-toggle-wrapper>
          </vdr-form-field>
          <vdr-form-field [label]="'Max Height'" class="clr-col-6" *ngIf="pageFullWidthControl.value">
            <select [formControl]="sectionMaxHeightControl">
              <option value="none">None (auto)</option>
              <option value="40vh">Small (40vh)</option>
              <option value="60vh">Medium (60vh)</option>
              <option value="80vh">Large (80vh)</option>
              <option value="100vh">Full Screen (100vh)</option>
            </select>
          </vdr-form-field>
        </div>
      </vdr-card>

      <!-- First Row Desktop: Width, Top Margin, Bottom Margin -->
      <vdr-card [title]="'Margin'">
      <div class="clr-row">
        <!-- Width Input Field -->
        <vdr-form-field [label]="'Width (0-1)'" class="clr-col-4">
          <input
            type="number"
            [formControl]="widthControl"
            min="0"
            max="1"
            step="0.01"
          >
        </vdr-form-field>

        <!-- Top Margin Input Field -->
        <vdr-form-field [label]="'Top Margin'" class="clr-col-4">
          <input
            type="number"
            [formControl]="topMarginControl"
            min="0"
            step="1"
          >
        </vdr-form-field>

        <!-- Bottom Margin Input Field -->
        <vdr-form-field [label]="'Bottom Margin'" class="clr-col-4">
          <input
            type="number"
            [formControl]="bottomMarginControl"
            min="0"
            step="1"
          >
        </vdr-form-field>
      </div>

      <!-- Second Row: Mobile Width, Mobile Top Margin, Mobile Bottom Margin -->
      <div class="clr-row">
        <!-- Mobile Width Input Field -->
        <vdr-form-field [label]="'Mobile Width (0-1)'" class="clr-col-4">
          <input
            type="number"
            [formControl]="mobileWidthControl"
            min="0"
            max="1"
            step="0.01"
          >
        </vdr-form-field>

        <!-- Mobile Top Margin Input Field -->
        <vdr-form-field [label]="'Mobile Top Margin'" class="clr-col-4">
          <input
            type="number"
            [formControl]="mobileTopMarginControl"
            min="0"
            step="1"
          >
        </vdr-form-field>

        <!-- Mobile Bottom Margin Input Field -->
        <vdr-form-field [label]="'Mobile Bottom Margin'" class="clr-col-4">
          <input
            type="number"
            [formControl]="mobileBottomMarginControl"
            min="0"
            step="1"
          >
        </vdr-form-field>
      </div>
      </vdr-card>

      <!-- Third Row Desktop: Horizontal Padding, Top Padding, Bottom Padding -->
      <vdr-card [title]="'Padding'">
      <div class="clr-row">
        <!-- Horizontal Padding Input Field -->
        <vdr-form-field [label]="'Horizontal Padding'" class="clr-col-4">
          <input
            type="number"
            [formControl]="horizontalPaddingControl"
            min="0"
            step="1"
          >
        </vdr-form-field>

        <!-- Top Padding Input Field -->
        <vdr-form-field [label]="'Top Padding'" class="clr-col-4">
          <input
            type="number"
            [formControl]="topPaddingControl"
            min="0"
            step="1"
          >
        </vdr-form-field>

        <!-- Bottom Padding Input Field -->
        <vdr-form-field [label]="'Bottom Padding'" class="clr-col-4">
          <input
            type="number"
            [formControl]="bottomPaddingControl"
            min="0"
            step="1"
          >
        </vdr-form-field>
      </div>

      <!-- Fourth Row: Mobile Horizontal Padding, Mobile Top Padding, Mobile Bottom Padding -->
      <div class="clr-row">
        <!-- Mobile Horizontal Padding Input Field -->
        <vdr-form-field [label]="'Mobile Horizontal Padding'" class="clr-col-4">
          <input
            type="number"
            [formControl]="mobileHorizontalPaddingControl"
            min="0"
            step="1"
          >
        </vdr-form-field>

        <!-- Mobile Top Padding Input Field -->
        <vdr-form-field [label]="'Mobile Top Padding'" class="clr-col-4">
          <input
            type="number"
            [formControl]="mobileTopPaddingControl"
            min="0"
            step="1"
          >
        </vdr-form-field>

        <!-- Mobile Bottom Padding Input Field -->
        <vdr-form-field [label]="'Mobile Bottom Padding'" class="clr-col-4">
          <input
            type="number"
            [formControl]="mobileBottomPaddingControl"
            min="0"
            step="1"
          >
        </vdr-form-field>
      </div>
      </vdr-card>
      <vdr-card [title]="'Section Rounded Corner Style'">
      <div class="clr-row">
        <vdr-form-field [label]="'Section Rounded Corners'" class="clr-col-6">
          <select [formControl]="sectionRoundedCornersControl">
            <option value="none">None</option>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
            <option value="full">Full</option>
          </select>
        </vdr-form-field>
        <vdr-form-field [label]="'Mobile Section Rounded Corners'" class="clr-col-6">
          <select [formControl]="mobileSectionRoundedCornersControl">
            <option value="none">None</option>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
            <option value="full">Full</option>
          </select>
        </vdr-form-field>
      </div>
      </vdr-card>
    </div>
  `,
  standalone: true,
  imports: [SharedModule],
  styleUrls: ['../styles.scss'],
})
export class LayoutOptionInputComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  layoutOptionData: LayoutOptionData;

  // Form controls
  pageFullWidthControl = new FormControl(false);
  sectionMaxHeightControl = new FormControl('none');
  sectionNameControl = new FormControl();
  layoutComponentTypeControl = new FormControl('', [Validators.required]);
  widthControl = new FormControl();
  topMarginControl = new FormControl();
  bottomMarginControl = new FormControl();
  mobileWidthControl = new FormControl();
  mobileTopMarginControl = new FormControl();
  mobileBottomMarginControl = new FormControl();
  horizontalPaddingControl = new FormControl();
  topPaddingControl = new FormControl();
  bottomPaddingControl = new FormControl();
  mobileHorizontalPaddingControl = new FormControl();
  mobileTopPaddingControl = new FormControl();
  mobileBottomPaddingControl = new FormControl();
  sectionRoundedCornersControl = new FormControl();
  mobileSectionRoundedCornersControl = new FormControl();
  forPageComponent = false;

  constructor(private notificationService: NotificationService) {}

  ngOnInit(): void {
    this.forPageComponent = this.config?.ui?.options?.forPageComponent ?? false;
    const defaultLayoutOptionData: LayoutOptionData = {
      sectionName: '',
      layoutComponentType: LayoutComponentType.Unknown,
      pageFullWidth: false,
      sectionMaxHeight: 'none',
      width: 1,
      topMargin: 0,
      bottomMargin: 0,
      mobileWidth: 1,
      mobileTopMargin: 0,
      mobileBottomMargin: 0,
      horizontalPadding: 0,
      topPadding: 0,
      bottomPadding: 0,
      mobileHorizontalPadding: 0,
      mobileTopPadding: 0,
      mobileBottomPadding: 0,
      sectionRoundedCorners: 'none',
      mobileSectionRoundedCorners: 'none',
    };
    this.layoutOptionData = safeJSONParse<LayoutOptionData>(
      this.formControl.value,
      this.notificationService,
      defaultLayoutOptionData,
    )!;
    this.initializeFormControls();
  }

  ngAfterViewInit() {
    // Subscribe to form control value changes and update layoutOptionData accordingly
    this.pageFullWidthControl.valueChanges.subscribe(this.onPageFullWidthChange);
    this.sectionMaxHeightControl.valueChanges.subscribe(this.onSectionMaxHeightChange);
    this.sectionNameControl.valueChanges.subscribe(this.onSectionNameChange);
    this.layoutComponentTypeControl.valueChanges.subscribe(this.onLayoutComponentTypeChange);
    this.widthControl.valueChanges.subscribe(this.onWidthChange);
    this.topMarginControl.valueChanges.subscribe(this.onTopMarginChange);
    this.bottomMarginControl.valueChanges.subscribe(this.onBottomMarginChange);
    this.mobileWidthControl.valueChanges.subscribe(this.onMobileWidthChange);
    this.mobileTopMarginControl.valueChanges.subscribe(this.onMobileTopMarginChange);
    this.mobileBottomMarginControl.valueChanges.subscribe(this.onMobileBottomMarginChange);
    this.horizontalPaddingControl.valueChanges.subscribe(this.onHorizontalPaddingChange);
    this.topPaddingControl.valueChanges.subscribe(this.onTopPaddingChange);
    this.bottomPaddingControl.valueChanges.subscribe(this.onBottomPaddingChange);
    this.mobileHorizontalPaddingControl.valueChanges.subscribe(this.onMobileHorizontalPaddingChange);
    this.mobileTopPaddingControl.valueChanges.subscribe(this.onMobileTopPaddingChange);
    this.mobileBottomPaddingControl.valueChanges.subscribe(this.onMobileBottomPaddingChange);
    this.sectionRoundedCornersControl.valueChanges.subscribe(this.onSectionRoundedCornersChange);
    this.mobileSectionRoundedCornersControl.valueChanges.subscribe(this.onMobileSectionRoundedCornersChange);
  }

  // Initialize form controls with values from layoutOptionData
  private initializeFormControls() {
    this.pageFullWidthControl.setValue(this.layoutOptionData?.pageFullWidth ?? false);
    this.sectionMaxHeightControl.setValue(this.layoutOptionData?.sectionMaxHeight || 'none');
    this.sectionNameControl.setValue(this.layoutOptionData?.sectionName);
    this.layoutComponentTypeControl.setValue(this.layoutOptionData?.layoutComponentType || '');
    this.widthControl.setValue(this.layoutOptionData?.width);
    this.topMarginControl.setValue(this.layoutOptionData?.topMargin);
    this.bottomMarginControl.setValue(this.layoutOptionData?.bottomMargin);
    this.mobileWidthControl.setValue(this.layoutOptionData?.mobileWidth);
    this.mobileTopMarginControl.setValue(this.layoutOptionData?.mobileTopMargin);
    this.mobileBottomMarginControl.setValue(this.layoutOptionData?.mobileBottomMargin);
    this.horizontalPaddingControl.setValue(this.layoutOptionData?.horizontalPadding);
    this.topPaddingControl.setValue(this.layoutOptionData?.topPadding);
    this.bottomPaddingControl.setValue(this.layoutOptionData?.bottomPadding);
    this.mobileHorizontalPaddingControl.setValue(this.layoutOptionData?.mobileHorizontalPadding);
    this.mobileTopPaddingControl.setValue(this.layoutOptionData?.mobileTopPadding);
    this.mobileBottomPaddingControl.setValue(this.layoutOptionData?.mobileBottomPadding);
    this.sectionRoundedCornersControl.setValue(this.layoutOptionData?.sectionRoundedCorners || 'none');
    this.mobileSectionRoundedCornersControl.setValue(this.layoutOptionData?.mobileSectionRoundedCorners || 'none');
  }

  // Value change handlers for form controls
  private onPageFullWidthChange = (value: boolean | null) => {
    const newValue = value ?? false;
    if (this.layoutOptionData?.pageFullWidth !== newValue) {
      this.layoutOptionData!.pageFullWidth = newValue;
      this.updateJsonString();
    }
  };

  private onSectionMaxHeightChange = (value: string | null) => {
    const newValue = value || 'none';
    if (this.layoutOptionData?.sectionMaxHeight !== newValue) {
      this.layoutOptionData!.sectionMaxHeight = newValue;
      this.updateJsonString();
    }
  };

  private onSectionNameChange = (value: string) => {
    if (this.layoutOptionData?.sectionName !== value) {
      this.layoutOptionData!.sectionName = value;
      this.updateJsonString();
    }
  };

  private onLayoutComponentTypeChange = (value: string | null) => {
    if (value && this.layoutOptionData?.layoutComponentType !== value) {
      this.layoutOptionData!.layoutComponentType = value as LayoutComponentType;
      this.updateJsonString();
    }
  };

  private onWidthChange = (value: number) => {
    if (this.layoutOptionData?.width !== value) {
      this.layoutOptionData!.width = value;
      this.updateJsonString();
    }
  };

  private onTopMarginChange = (value: number) => {
    if (this.layoutOptionData?.topMargin !== value) {
      this.layoutOptionData!.topMargin = value;
      this.updateJsonString();
    }
  };

  private onBottomMarginChange = (value: number) => {
    if (this.layoutOptionData?.bottomMargin !== value) {
      this.layoutOptionData!.bottomMargin = value;
      this.updateJsonString();
    }
  };

  private onMobileWidthChange = (value: number) => {
    if (this.layoutOptionData?.mobileWidth !== value) {
      this.layoutOptionData!.mobileWidth = value;
      this.updateJsonString();
    }
  };

  private onMobileTopMarginChange = (value: number) => {
    if (this.layoutOptionData?.mobileTopMargin !== value) {
      this.layoutOptionData!.mobileTopMargin = value;
      this.updateJsonString();
    }
  };

  private onMobileBottomMarginChange = (value: number) => {
    if (this.layoutOptionData?.mobileBottomMargin !== value) {
      this.layoutOptionData!.mobileBottomMargin = value;
      this.updateJsonString();
    }
  };

  private onHorizontalPaddingChange = (value: number) => {
    if (this.layoutOptionData?.horizontalPadding !== value) {
      this.layoutOptionData!.horizontalPadding = value;
      this.updateJsonString();
    }
  };

  private onTopPaddingChange = (value: number) => {
    if (this.layoutOptionData?.topPadding !== value) {
      this.layoutOptionData!.topPadding = value;
      this.updateJsonString();
    }
  };

  private onBottomPaddingChange = (value: number) => {
    if (this.layoutOptionData?.bottomPadding !== value) {
      this.layoutOptionData!.bottomPadding = value;
      this.updateJsonString();
    }
  };

  private onMobileHorizontalPaddingChange = (value: number) => {
    if (this.layoutOptionData?.mobileHorizontalPadding !== value) {
      this.layoutOptionData!.mobileHorizontalPadding = value;
      this.updateJsonString();
    }
  };

  private onMobileTopPaddingChange = (value: number) => {
    if (this.layoutOptionData?.mobileTopPadding !== value) {
      this.layoutOptionData!.mobileTopPadding = value;
      this.updateJsonString();
    }
  };

  private onMobileBottomPaddingChange = (value: number) => {
    if (this.layoutOptionData?.mobileBottomPadding !== value) {
      this.layoutOptionData!.mobileBottomPadding = value;
      this.updateJsonString();
    }
  };

  private onSectionRoundedCornersChange = (value: string) => {
    if (this.layoutOptionData?.sectionRoundedCorners !== value) {
      this.layoutOptionData!.sectionRoundedCorners = value;
      this.updateJsonString();
    }
  };

  private onMobileSectionRoundedCornersChange = (value: string) => {
    if (this.layoutOptionData?.mobileSectionRoundedCorners !== value) {
      this.layoutOptionData!.mobileSectionRoundedCorners = value;
      this.updateJsonString();
    }
  };

  // Update the JSON string and form control value
  private updateJsonString() {
    const jsonString = JSON.stringify(this.layoutOptionData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}